import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtMoney } from "@/lib/format";
import { WidgetError } from "@/components/WidgetError";
import { AlertTriangle, Clock, Ban, Timer, ChevronLeft, ChevronRight } from "lucide-react";

const StatCard = ({ label, value, testId, tone = "default", sublabel }) => {
  const toneCls = {
    default: "text-zinc-950",
    good: "text-green-600",
    warn: "text-yellow-700",
    bad: "text-red-600",
    info: "text-blue-600",
  }[tone];
  return (
    <div className="bg-white border border-zinc-200 p-5 rounded" data-testid={testId}>
      <div className="label-overline">{label}</div>
      <div className={`mt-2 text-2xl sm:text-3xl font-black font-mono ${toneCls} break-words`}>{value}</div>
      {sublabel && <div className="mt-1 text-[11px] text-zinc-500 font-mono">{sublabel}</div>}
    </div>
  );
};

const AlertCard = ({ label, count, icon: Icon, tone, to, testId }) => {
  const badge = {
    red: "bg-red-50 border-red-200 text-red-700",
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
  }[tone];
  return (
    <Link to={to} data-testid={testId}
          className={`block rounded border ${badge} p-4 hover:shadow transition min-w-0`}>
      <div className="flex items-center justify-between">
        <Icon size={16} />
        <div className="text-2xl font-black font-mono">{count}</div>
      </div>
      <div className="label-overline mt-1 truncate">{label}</div>
    </Link>
  );
};

function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function shiftMonth(iso, delta) {
  const [y, m] = iso.split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
function labelMonth(iso) {
  const [y, m] = iso.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-ZA", { month: "long", year: "numeric" });
}

export default function PlatformDashboard() {
  const [month, setMonth] = useState(currentMonthISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    api.get("/platform/overview", { params: { month } })
       .then(({ data }) => { if (!cancelled) setData(data); })
       .catch((err) => { if (!cancelled) setError(err); })
       .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month, reloadKey]);

  const retry = useCallback(() => setReloadKey((k) => k + 1), []);
  const isCurrent = month === currentMonthISO();

  const monthOptions = useMemo(() => {
    // Last 12 months + current
    const now = new Date();
    const out = [];
    for (let i = 0; i < 12; i += 1) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      out.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
    return out;
  }, []);

  return (
    <div className="min-w-0">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="label-overline">Overview</div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight mt-1">Platform Console</h1>
          <p className="text-zinc-600 mt-1 text-sm">Monthly billing view. Multi-tenant health.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="platform-month-prev"
                  onClick={() => setMonth(shiftMonth(month, -1))}
                  className="w-9 h-9 rounded border border-slate-300 hover:bg-slate-50 grid place-items-center">
            <ChevronLeft size={16} />
          </button>
          <select data-testid="platform-month-select"
                  value={month} onChange={(e) => setMonth(e.target.value)}
                  className="h-9 border border-slate-300 rounded px-2 font-mono text-sm bg-white">
            {monthOptions.map((m) => (
              <option key={m} value={m}>{labelMonth(m)}</option>
            ))}
          </select>
          <button data-testid="platform-month-next"
                  onClick={() => setMonth(shiftMonth(month, 1))}
                  disabled={isCurrent}
                  className="w-9 h-9 rounded border border-slate-300 hover:bg-slate-50 grid place-items-center disabled:opacity-40 disabled:cursor-not-allowed">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4">
          <WidgetError error={error} onRetry={retry} title="Could not load platform overview" testId="platform-error" />
        </div>
      )}

      {loading && !data ? (
        <div className="text-zinc-500 label-overline">Loading…</div>
      ) : data && (
        <>
          {/* Monthly money cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard testId="stat-income"
                      label="Expected income"
                      sublabel={labelMonth(month)}
                      value={fmtMoney(data.income_expected_cents)} />
            <StatCard testId="stat-received"
                      label="Payments received"
                      sublabel={labelMonth(month)}
                      value={fmtMoney(data.payments_received_cents)} tone="good" />
            <StatCard testId="stat-outstanding"
                      label="Outstanding / overdue"
                      sublabel={labelMonth(month)}
                      value={fmtMoney(data.outstanding_cents)}
                      tone={data.outstanding_cents > 0 ? "warn" : "default"} />
          </div>

          {/* Alert cards */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
            <AlertCard testId="alert-overdue" tone="red"
                       label="Overdue" count={data.alert_overdue}
                       icon={AlertTriangle}
                       to="/platform/tenants?status=active" />
            <AlertCard testId="alert-grace" tone="amber"
                       label="Grace ending ≤3d" count={data.alert_grace_ending_soon}
                       icon={Clock}
                       to="/platform/tenants?status=grace" />
            <AlertCard testId="alert-suspended" tone="slate"
                       label="Suspended" count={data.alert_suspended}
                       icon={Ban}
                       to="/platform/tenants?status=suspended" />
            <AlertCard testId="alert-trial" tone="blue"
                       label="Trials ending ≤3d" count={data.alert_trial_ending_soon}
                       icon={Timer}
                       to="/platform/tenants?status=trial" />
          </div>

          {/* Tenant status totals */}
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatCard testId="stat-active" label="Active tenants" value={data.tenants_active} tone="good" />
            <StatCard testId="stat-grace" label="Grace" value={data.tenants_grace} tone="warn" />
            <StatCard testId="stat-suspended-count" label="Suspended" value={data.tenants_suspended} tone="bad" />
            <StatCard testId="stat-trial" label="Trial" value={data.tenants_trial} tone="info" />
            <StatCard testId="stat-cancelled" label="Cancelled" value={data.tenants_cancelled} />
          </div>
        </>
      )}
    </div>
  );
}
